import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Doughnut, Pie, Bar } from 'react-chartjs-2';
import { fetchClients } from '../slices/clientSlice';
import { fetchOrders } from '../slices/ordersSlice';
import { useNavigate, Link } from 'react-router-dom';
import {
	PlusCircle, 
	UserPlus, 
	TrendingUp, 
	CheckCircle, 
	XCircle,
	ArrowRight,
	Activity,
    Calendar,
	Layers
} from 'react-feather';
import {
	Chart,
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	ArcElement,
	DoughnutController,
	PieController,
	BarController,
	Tooltip,
	Legend,
} from 'chart.js';
import ClientsDashboard from '../pages/ClientsDashboard';
import InfoTooltip from './common/InfoTooltip';

Chart.register(
	CategoryScale,
	LinearScale,
	BarElement,
	PointElement,
	LineElement,
	ArcElement,
	DoughnutController,
	PieController,
	BarController,
	Tooltip,
	Legend
);


export default function SummaryBtns() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [yearFilter, setYearFilter] = useState(new Date().getFullYear());

	useEffect(() => {
		dispatch(fetchClients({ pageSize: 1000 })); // Fetch more for stats
		dispatch(fetchOrders({ pageSize: 1000, includeInvoices: true }));
	}, [dispatch]);

	const clients = useSelector((state) => state.clients.items);
	const orders = useSelector((state) => state.orders.items);
	const selfPermissions = useSelector((state) => state?.auth?.self?.role);

	// Filter orders by year
	const filteredOrders = useMemo(() => {
		if (!orders) return [];
		return orders.filter(order => {
			const date = new Date(order.createdAt);
			return date.getFullYear() === parseInt(yearFilter);
		});
	}, [orders, yearFilter]);

	// Billing Chart Data
	const billingByMonth = useMemo(() => {
		const months = Array(12).fill(0);
		filteredOrders.forEach(order => {
			if (order.invoices && order.invoices.length > 0) {
				const month = new Date(order.createdAt).getMonth();
				// Sum up dueAmount or paidAmount. We use dueAmount as rough revenue estimation for now.
				order.invoices.forEach(inv => {
					months[month] += inv.dueAmount || 0;
				});
			}
		});
		return months;
	}, [filteredOrders]);

	if (!clients || !orders) {
		return <p className="p-10">Loading data...</p>;
	}

	const clientsByStatus = clients.reduce((acc, client) => {
		const { status } = client;
		if (!acc[status]) {
			acc[status] = [];
		}
		acc[status].push(client);
		return acc;
	}, {});

	// Use filtered orders for KPI
	const ordersByStatus = filteredOrders.reduce((acc, order) => {
		const { status } = order;
		if (!acc[status]) {
			acc[status] = [];
		}
		acc[status].push(order);
		return acc;
	}, {});

	// KPI Calculations
	const totalQuotes = ordersByStatus[1]?.length || 0;
	const activeOrders = (ordersByStatus[2]?.length || 0) + 
						 (ordersByStatus[3]?.length || 0) + 
						 (ordersByStatus[4]?.length || 0) + 
						 (ordersByStatus[5]?.length || 0);
	const completedOrders = ordersByStatus[10]?.length || 0;
	const canceledOrders = ordersByStatus[-1]?.length || 0;

	const dataOrderStatus = {
		labels: ['Quotes', 'In Progress', 'Done', 'Canceled'],
		datasets: [
			{
				data: [totalQuotes, activeOrders, completedOrders, canceledOrders],
				backgroundColor: [
					'rgba(0, 180, 219, 0.8)',
					'rgba(94, 231, 223, 0.8)',
					'rgba(240, 147, 251, 0.8)',
					'rgba(100, 116, 139, 0.8)',
				],
				hoverOffset: 10,
				borderWidth: 0,
			},
		],
	};

	const dataClientStatus = {
		labels: ['Pending', 'Active', 'Blocked'],
		datasets: [
			{
				data: [
					clientsByStatus[1]?.length || 0,
					clientsByStatus[2]?.length || 0,
					clientsByStatus[3]?.length || 0,
				],
				backgroundColor: [
					'rgba(246, 211, 101, 0.8)',
					'rgba(16, 185, 129, 0.8)',
					'rgba(239, 68, 68, 0.8)',
				],
				borderWidth: 0,
			},
		],
	};

	const dataBilling = {
		labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
		datasets: [
			{
				label: 'Revenue',
				data: billingByMonth,
				backgroundColor: 'rgba(59, 130, 246, 0.8)',
				borderRadius: 4,
			}
		]
	};

	const chartOptions = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'bottom',
				labels: {
					usePointStyle: true,
					padding: 20,
					font: { size: 12, weight: '500' }
				}
			},
			tooltip: {
				padding: 12,
				backgroundColor: 'rgba(30, 41, 59, 0.9)',
			}
		}
	};

	const barOptions = {
		...chartOptions,
		scales: {
			y: {
				beginAtZero: true,
				grid: {
					display: true,
					drawBorder: false,
					color: 'rgba(0, 0, 0, 0.05)'
				}
			},
			x: {
				grid: {
					display: false
				}
			}
		}
	};

    const handleYearChange = (e) => {
        setYearFilter(e.target.value);
    };

    const currentYear = new Date().getFullYear();
    const years = [currentYear, currentYear - 1, currentYear - 2];

	return (
		<div className="modern-dashboard">
			<div className="flex justify-between items-center mb-8">
				<h1 className="text-3xl font-bold text-slate-800">Dashboard Overview</h1>
				<div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 shadow-sm">
                        <Calendar size={16} className="text-slate-500" />
                        <select 
                            value={yearFilter} 
                            onChange={handleYearChange}
                            className="bg-transparent outline-none text-sm font-medium text-slate-700 cursor-pointer"
                        >
                            {years.map(year => (
                                <option key={year} value={year}>{year}</option>
                            ))}
                        </select>
                    </div>
					<div className="text-sm text-slate-500 font-medium">
						{new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
					</div>
				</div>
			</div>

			{/* Quick Access Bar */}
			<div className="quick-access-bar">
				<button onClick={() => navigate('/leads')} className="quick-access-btn primary">
					<PlusCircle size={20} />
					<span>New Quote</span>
				</button>
				<button
					onClick={() => navigate('/dashboard/client-management')}
					className="quick-access-btn"
				>
					<Layers size={20} />
					<span>Client Hub</span>
				</button>
				<button onClick={() => {
					navigate('/clients', { state: { openCreateModal: true } });
				}} className="quick-access-btn">
					<UserPlus size={20} />
					<span>New Client</span>
				</button>
			</div>

			{/* KPI Grid */}
			<div className="kpi-grid">
				<div className="kpi-card kpi-quotes">
					<span className="kpi-label flex items-center">
                        Total Quotes
                        <InfoTooltip text="Total number of quotes generated this year (Status: Quote)." />
                    </span>
					<div className="flex items-end justify-between">
						<span className="kpi-value">{totalQuotes}</span>
						<TrendingUp size={24} className="text-blue-500 opacity-20" />
					</div>
				</div>
				<div className="kpi-card kpi-active">
					<span className="kpi-label flex items-center">
                        Active Orders
                        <InfoTooltip text="Orders currently in progress (Status: 2-9)." />
                    </span>
					<div className="flex items-end justify-between">
						<span className="kpi-value">{activeOrders}</span>
						<Activity size={24} className="text-green-500 opacity-20" />
					</div>
				</div>
				<div className="kpi-card kpi-done">
					<span className="kpi-label flex items-center">
                        Completed Projects
                        <InfoTooltip text="Orders marked as completed this year." />
                    </span>
					<div className="flex items-end justify-between">
						<span className="kpi-value">{completedOrders}</span>
						<CheckCircle size={24} className="text-purple-500 opacity-20" />
					</div>
				</div>
				<div className="kpi-card kpi-canceled">
					<span className="kpi-label flex items-center">
                        Canceled
                        <InfoTooltip text="Orders canceled this year." />
                    </span>
					<div className="flex items-end justify-between">
						<span className="kpi-value">{canceledOrders}</span>
						<XCircle size={24} className="text-slate-500 opacity-20" />
					</div>
				</div>
			</div>

			{/* Charts Grid */}
			<div className="charts-grid input-group grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
				<div className="chart-card">
					<h3>
						Order Status
						<Link to="/orders" className="view-all-link">
							View all orders <ArrowRight size={14} className="inline ml-1" />
						</Link>
					</h3>
					<div className="chart-container-inner">
						<Doughnut data={dataOrderStatus} options={chartOptions} />
					</div>
				</div>

				<div className="chart-card">
					<h3>
						Client Distribution
						<Link to="/clients" className="view-all-link">
							Manage clients <ArrowRight size={14} className="inline ml-1" />
						</Link>
					</h3>
					<div className="chart-container-inner">
						<Pie data={dataClientStatus} options={chartOptions} />
					</div>
				</div>

                <div className="chart-card">
					<h3>
						Revenue ({yearFilter})
						<Link to="/billing" className="view-all-link">
							Billing <ArrowRight size={14} className="inline ml-1" />
						</Link>
					</h3>
					<div className="chart-container-inner">
						<Bar data={dataBilling} options={barOptions} />
					</div>
				</div>
			</div>

			{/* Tables Section */}
			<div className="table-card">
				{(selfPermissions === 'SUPER' || selfPermissions === 'ADMIN') && (
					<ClientsDashboard isModern={true} externalOrders={orders} />
				)}
			</div>
		</div>
	);
}

const ActivityIcon = ({ size, className }) => (
	<svg 
		width={size} 
		height={size} 
		viewBox="0 0 24 24" 
		fill="none" 
		stroke="currentColor" 
		strokeWidth="2" 
		strokeLinecap="round" 
		strokeLinejoin="round" 
		className={className}
	>
		<polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
	</svg>
);
